import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Box, Button, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, Paper, TextField, Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { cloGet, CLO_NETWORKS, ENDPOINTS, FEE_TEMPLATES, FEE_TYPES, HOLDOUT_SHARES_BP, RULE_TEMPLATES, RULE_TYPES } from "./api";
import { AFTER_CLEAR_REFUNDS, blankCohort, CohortForm, EXISTING_COHORT, VersionForm } from "./versionForm";

interface Props {
  value: VersionForm;
  onChange: (next: VersionForm) => void;
  liveVersion?: { version: number; start_date: string; end_date: string };
  campaignId?: number;
}

const VersionEditor: React.FC<Props> = ({ value, onChange, liveVersion, campaignId }) => {
  const [published, setPublished] = useState<number[]>([]);
  const set = (patch: Partial<VersionForm>) => onChange({ ...value, ...patch });
  const lastCount = useRef(value.cohorts.length);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (value.cohorts.length > lastCount.current) endRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    lastCount.current = value.cohorts.length;
  }, [value.cohorts.length]);

  useEffect(() => {
    if (!campaignId) return;
    cloGet(ENDPOINTS.CLO_COHORT_DEFINITIONS, { campaignId })
      .then((res) => setPublished((res.data || []).filter((d: any) => d.status === "published").map((d: any) => Number(d.version))))
      .catch(() => setPublished([]));
  }, [campaignId]);

  const latestPublished = published.length ? Math.max(...published) : null;
  const removeCohort = (uid: string) => {
    if (value.cohorts.length === 1) return;
    set({ cohorts: value.cohorts.filter((c) => c.uid !== uid) });
  };
  const setCohort = (uid: string, patch: Partial<CohortForm>) =>
    set({ cohorts: value.cohorts.map((c) => (c.uid === uid ? { ...c, ...patch } : c)) });

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>Dates (London time)</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth type="date" label="Start date" InputLabelProps={{ shrink: true }} value={value.startDate}
            onChange={(e) => set({ startDate: e.target.value })}
            helperText={liveVersion
              ? `After ${liveVersion.start_date} and no later than ${liveVersion.end_date}. Approve before it.`
              : "Today or later. Approve before it."}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth type="date" label="End date" InputLabelProps={{ shrink: true }} value={value.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
            helperText={liveVersion ? `Now ${liveVersion.end_date}. Extend or shorten.` : "Last day prices apply"}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            select fullWidth label="Cohort rules version" value={value.cohortDefinitionVersion}
            onChange={(e) => set({ cohortDefinitionVersion: e.target.value })}
            helperText={
              !campaignId
                ? "Save the campaign first, then add cohort rules in Open Banking & Cohorts › Cohort rules."
                : published.length
                  ? "Which rules decide each customer's cohort. Cohort names below must match the rule names."
                  : "No published cohort rules yet. Publish them in Open Banking & Cohorts › Cohort rules before approving."
            }
          >
            <MenuItem value="">{latestPublished ? `Latest published (now v${latestPublished})` : "Latest published (none yet)"}</MenuItem>
            {[...published].sort((a, b) => b - a).map((v) => <MenuItem key={v} value={String(v)}>v{v}</MenuItem>)}
            {value.cohortDefinitionVersion && !published.includes(Number(value.cohortDefinitionVersion)) && (
              <MenuItem value={value.cohortDefinitionVersion}>v{value.cohortDefinitionVersion} (not published)</MenuItem>
            )}
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>Campaign settings</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="textSecondary">Card networks</Typography>
          <Box>
            {CLO_NETWORKS.map((n) => (
              <FormControlLabel
                key={n}
                control={
                  <Checkbox
                    checked={value.networks.includes(n)}
                    onChange={(e) => set({ networks: e.target.checked ? [...value.networks, n] : value.networks.filter((x) => x !== n) })}
                  />
                }
                label={n}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth label="Budget cap (£)" value={value.campaignCap} onChange={(e) => set({ campaignCap: e.target.value })} helperText="Reward + fee. Empty = no cap" />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth label="VAT removed (%)" value={value.vatPercent} onChange={(e) => set({ vatPercent: e.target.value })} helperText="0 = use full amount" />
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControlLabel
            control={<Checkbox checked={value.activationRequired} onChange={(e) => set({ activationRequired: e.target.checked })} />}
            label="Customer must tap Activate"
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField select fullWidth label="Refund after the reward has cleared" value={value.afterClearRefund} onChange={(e) => set({ afterClearRefund: e.target.value })}
            helperText="The retailer's reward and fee are not taken back">
            {Object.entries(AFTER_CLEAR_REFUNDS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Pricing per cohort</Typography>
        <Button size="small" variant="outlined" color="primary" onClick={() => set({ cohorts: [...value.cohorts, blankCohort()] })}>+ Add cohort</Button>
      </Box>
      <Typography variant="caption" color="textSecondary" component="div">
        Each cohort here must have the same name as a cohort rule that gets offers, or approval is blocked.
        Rule and fee settings use the engine's units: rateBp is basis points (1000 = 10%), *Minor is pence.
        <br />
        <b>{EXISTING_COHORT}</b> is built in: it must be priced, can't be removed or renamed, and is never one-time.
        After a one-time reward (e.g. Competitor's 10%) customers move to {EXISTING_COHORT}, and customers who can't repeat a
        one-time cohort fall back to it.
      </Typography>
      {value.cohorts.map((c) => (
        <Paper key={c.uid} variant="outlined" style={{ padding: 12, marginTop: 12 }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={9} md={2}>
              <TextField
                fullWidth label="Cohort" value={c.cohortName} disabled={c.cohortName === EXISTING_COHORT}
                onChange={(e) => setCohort(c.uid, { cohortName: e.target.value })}
                helperText={c.cohortName === EXISTING_COHORT ? "Built-in: after a one-time reward, and the fallback" : undefined}
              />
            </Grid>
            <Grid item xs={3} md={2}>
              <FormControlLabel
                control={<Checkbox checked={c.cohortName !== EXISTING_COHORT && c.oneTime} disabled={c.cohortName === EXISTING_COHORT}
                  onChange={(e) => setCohort(c.uid, { oneTime: e.target.checked })} />}
                label="One-time"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select fullWidth label="Hold-out" value={c.holdoutShareBp} onChange={(e) => setCohort(c.uid, { holdoutShareBp: Number(e.target.value) })}>
                {HOLDOUT_SHARES_BP.map((bp) => <MenuItem key={bp} value={bp}>{bp / 100}%</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField fullWidth label="Min spend £" value={c.minSpend} onChange={(e) => setCohort(c.uid, { minSpend: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth label="Cap per purchase £" value={c.transactionCap} onChange={(e) => setCohort(c.uid, { transactionCap: e.target.value })} />
            </Grid>
            <Grid item xs={5} md={1}>
              <TextField fullWidth label="Cohort cap £" value={c.cohortCap} onChange={(e) => setCohort(c.uid, { cohortCap: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={2} style={{ textAlign: "right" }}>
              <IconButton size="small" title={c.cohortName === EXISTING_COHORT ? `${EXISTING_COHORT} can't be removed` : "Remove cohort"} color="secondary"
                disabled={value.cohorts.length === 1 || c.cohortName === EXISTING_COHORT} onClick={() => removeCohort(c.uid)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth label="Customer reward rule" value={c.ruleType}
                onChange={(e) => setCohort(c.uid, { ruleType: e.target.value, ruleParamsText: JSON.stringify(RULE_TEMPLATES[e.target.value], null, 2) })}
              >
                {Object.entries(RULE_TYPES).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
              </TextField>
              <TextField fullWidth multiline rows={c.ruleType === "E" ? 8 : 3} margin="dense" variant="outlined" label="Rule settings (JSON)" value={c.ruleParamsText} onChange={(e) => setCohort(c.uid, { ruleParamsText: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth label="Sprive fee" value={c.feeType}
                onChange={(e) => setCohort(c.uid, { feeType: e.target.value, feeParamsText: JSON.stringify(FEE_TEMPLATES[e.target.value], null, 2) })}
              >
                {Object.entries(FEE_TYPES).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
              </TextField>
              <TextField fullWidth multiline rows={3} margin="dense" variant="outlined" label="Fee settings (JSON)" value={c.feeParamsText} onChange={(e) => setCohort(c.uid, { feeParamsText: e.target.value })} />
              {c.ruleType === "E" && (
                <Typography variant="caption" color="textSecondary">With rule E, each channel's own fee applies and this fee is not used. Add a "default" entry to price channels that are not listed.</Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      ))}
      <div ref={endRef} />
    </Box>
  );
};

export default VersionEditor;
